import { getExplicitVariantId } from './explicit-variant-selection.js';

let completeTheLookInstanceCount = 0;

/**
 * UI, shared selection state, size synchronization, summary, and cart-line
 * source for the Complete the Look product block.
 */
class CompleteTheLook extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {CompleteTheLookState} */
  state;

  /** @type {Record<string, string>} */
  #mainVariantSizeMap = {};

  /** @type {number} */
  #instanceId = 0;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    completeTheLookInstanceCount += 1;
    this.#instanceId = completeTheLookInstanceCount;
    this.#ensureUniqueRadioControls();
    this.#mainVariantSizeMap = this.#readMainVariantSizeMap();
    const initialVariantId = this.dataset.mainVariantId || null;
    const explicitVariantId = !this.closest('quick-add-dialog')
      ? getExplicitVariantId(window.location.href, this.#getProductForm()?.dataset.selectedVariantId)
      : null;

    this.state = {
      mainProduct: {
        productId: this.dataset.mainProductId || null,
        productTitle: this.dataset.mainProductTitle || '',
        summaryLabel: this.dataset.mainSummaryLabel || this.dataset.mainProductTitle || '',
        selectedVariantId: initialVariantId,
        selectedSize: initialVariantId ? this.#mainVariantSizeMap[initialVariantId] || '' : '',
        explicitlySelected: Boolean(explicitVariantId),
        sizeOptionPosition: this.#parsePosition(this.dataset.mainSizeOptionPosition),
        quantity: 1,
        priceCents: null,
      },
      fittedSheet: this.#createCompanionState('fittedSheet'),
      flatSheet: this.#createCompanionState('flatSheet'),
    };

    const { signal } = this.#abortController;
    const productSection = this.closest('.shopify-section');
    productSection?.addEventListener('variant:update', this.#handleMainVariantUpdate, { signal });

    const productForm = this.#getProductForm();
    if (productForm) {
      productForm.addEventListener('quantity-selector:update', this.#handleMainQuantityChange, { signal });
      productForm.addEventListener('input', this.#handleMainQuantityChange, { signal });
      productForm.addEventListener('product-form:variant-rendered', this.#handleProductFormVariantRendered, {
        signal,
      });
      this.state.mainProduct.quantity = this.#readMainQuantity(productForm);
    }

    if (productForm && productForm.dataset.productId !== String(this.state.mainProduct.productId || '')) {
      console.warn('[Complete the Look] Main product identity does not match the associated product form.', {
        completeTheLookProductId: this.state.mainProduct.productId,
        productFormProductId: productForm?.dataset.productId || null,
      });
    }

    this.querySelectorAll('[data-companion]').forEach((card) => {
      card.querySelector('[data-role="checkbox"]')?.addEventListener('change', this.#handleCheckboxChange, { signal });
      card.querySelectorAll('[data-role="size-option"]').forEach((option) => {
        option.addEventListener('change', this.#handleSizeOptionChange, { signal });
      });
    });

    this.#renderAll();
    this.#dispatchStateChange('initial');
  }

  #ensureUniqueRadioControls() {
    const instanceKey = `${this.dataset.instanceKey || 'complete-the-look'}-${this.#instanceId}`;

    this.querySelectorAll('[data-companion]').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      const companionKey = card.dataset.companion || 'companion';
      card.querySelectorAll('[data-role="size-option"]').forEach((option, index) => {
        if (!(option instanceof HTMLInputElement)) return;
        const id = `CompleteTheLook-${instanceKey}-${companionKey}-${index}-${option.value}`;
        option.name = `ctl-${instanceKey}-${companionKey}-size`;
        option.id = id;
        const label = option.closest('label');
        if (label instanceof HTMLLabelElement) label.htmlFor = id;
      });
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #readMainVariantSizeMap() {
    return this.#readJsonData('[data-main-variant-size-map]');
  }

  /** @param {string} selector */
  #readJsonData(selector) {
    const data = this.querySelector(selector)?.textContent;
    if (!data) return {};
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      console.warn(`Complete the Look could not parse JSON data from ${selector}.`, error);
      return {};
    }
  }

  /**
   * Returns a defensive snapshot for future summary and cart integrations.
   * @returns {CompleteTheLookState}
   */
  getState() {
    return structuredClone(this.state);
  }

  /** @param {string} key */
  #createCompanionState(key) {
    const card = this.querySelector(`[data-companion="${key}"]`);
    const checkbox = card?.querySelector('[data-role="checkbox"]');
    const selectedOption = card?.querySelector('[data-role="size-option"]:checked');
    const hasAvailableVariant = Array.from(card?.querySelectorAll('[data-role="size-option"]') || []).some(
      (option) => option instanceof HTMLInputElement && Boolean(option.value) && option.dataset.available === 'true'
    );

    return {
      checked: checkbox instanceof HTMLInputElement ? checkbox.checked : false,
      productId: card instanceof HTMLElement ? card.dataset.productId || null : null,
      productTitle: card instanceof HTMLElement ? card.dataset.productTitle || '' : '',
      summaryLabel: card instanceof HTMLElement ? card.dataset.summaryLabel || '' : '',
      initialVariantId: selectedOption instanceof HTMLInputElement ? selectedOption.value || null : null,
      selectedVariantId: selectedOption instanceof HTMLInputElement ? selectedOption.value || null : null,
      selectedSize: selectedOption?.dataset.size || '',
      available: selectedOption?.dataset.available === 'true',
      price: selectedOption?.dataset.price || '',
      priceCents: this.#parseCents(selectedOption?.dataset.priceCents),
      compareAtPrice: selectedOption?.dataset.compareAtPrice || '',
      compareAtPriceCents: this.#parseCents(selectedOption?.dataset.compareAtPriceCents),
      hasAvailableVariant,
    };
  }

  #handleCheckboxChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const card = event.target.closest('[data-companion]');
    const key = card instanceof HTMLElement ? card.dataset.companion : undefined;
    if (!this.#isCompanionKey(key)) return;

    this.state[key].checked = event.target.checked;
    if (!event.target.checked) this.#resetCompanionSelection(key);
    this.#renderCompanion(key);
    this.#dispatchStateChange(key);
  };

  #handleSizeOptionChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.dataset.role !== 'size-option') return;

    const card = event.target.closest('[data-companion]');
    const key = card instanceof HTMLElement ? card.dataset.companion : undefined;
    if (!this.#isCompanionKey(key)) return;

    if (event.target.disabled) return;
    card.querySelectorAll('[data-role="size-option"]').forEach((option) => {
      if (option instanceof HTMLInputElement) option.checked = option === event.target;
    });
    this.#updateCompanionFromControl(key, event.target);

    this.#renderCompanion(key);
    this.#dispatchStateChange(key);
  };

  #handleMainVariantUpdate = (event) => {
    const detail = event.detail;
    const eventProductId = detail?.data?.productId ? String(detail.data.productId) : null;
    const currentProductId = this.state.mainProduct.productId ? String(this.state.mainProduct.productId) : null;
    const isCombinedListingUpdate = Boolean(detail?.data?.newProduct);

    if (!isCombinedListingUpdate && eventProductId !== currentProductId) return;
    if (!(event.target instanceof HTMLElement) || event.target.tagName.toLowerCase() !== 'variant-picker') return;

    const pickerSection = event.target.dataset.sectionId;
    const currentSection = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
    if (pickerSection && currentSection && pickerSection !== currentSection) return;

    const resolvedProductId = detail?.data?.newProduct?.id || eventProductId || currentProductId;
    const resolvedVariantId = detail?.resource?.id ? String(detail.resource.id) : null;
    const resolvedSize = resolvedVariantId ? this.#mainVariantSizeMap[resolvedVariantId] || '' : '';

    Object.assign(this.state.mainProduct, {
      productId: resolvedProductId,
      selectedVariantId: resolvedVariantId,
      selectedSize: resolvedSize,
      explicitlySelected: event.target.dataset.explicitSelection === 'true',
    });

    this.#renderSelectionOutputs();

  };

  #handleProductFormVariantRendered = (event) => {
    const productForm = this.#getProductForm();
    if (!productForm || event.target !== productForm) return;
    const renderedVariantId = event.detail?.variantId ? String(event.detail.variantId) : null;
    if (renderedVariantId !== this.state.mainProduct.selectedVariantId) return;
    this.#renderSelectionOutputs();
  };

  #handleMainQuantityChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'quantity') return;
    const productForm = this.#getProductForm();
    if (!productForm || !productForm.contains(event.target)) return;

    const quantity = this.#readMainQuantity(productForm);
    if (quantity === this.state.mainProduct.quantity) return;

    this.state.mainProduct.quantity = quantity;
    this.#renderSelectionOutputs();
  };

  /**
   * @param {'fittedSheet' | 'flatSheet'} key
   * @param {HTMLInputElement} option
   */
  #updateCompanionFromControl(key, option) {
    Object.assign(this.state[key], {
      selectedVariantId: option.value || null,
      selectedSize: option.dataset.size || '',
      available: option.dataset.available === 'true',
      price: option.dataset.price || '',
      priceCents: this.#parseCents(option.dataset.priceCents),
      compareAtPrice: option.dataset.compareAtPrice || '',
      compareAtPriceCents: this.#parseCents(option.dataset.compareAtPriceCents),
      image: option.dataset.image || '',
      imageAlt: option.dataset.imageAlt || this.state[key].productTitle,
    });
  }

  /** @param {'fittedSheet' | 'flatSheet'} key */
  #resetCompanionSelection(key) {
    const card = this.querySelector(`[data-companion="${key}"]`);
    if (!(card instanceof HTMLElement)) return;

    const options = Array.from(card.querySelectorAll('[data-role="size-option"]'));
    const initialOption = options.find(
      (option) => option instanceof HTMLInputElement && option.value === this.state[key].initialVariantId
    );
    const fallbackOption = options.find(
      (option) => option instanceof HTMLInputElement && !option.disabled && option.dataset.available === 'true'
    );
    const resetOption = initialOption || fallbackOption;
    if (resetOption instanceof HTMLInputElement) this.#updateCompanionFromControl(key, resetOption);
  }

  #renderAll() {
    this.#renderCompanion('fittedSheet');
    this.#renderCompanion('flatSheet');
  }

  /** @param {'fittedSheet' | 'flatSheet'} key */
  #renderCompanion(key) {
    const card = this.querySelector(`[data-companion="${key}"]`);
    if (!(card instanceof HTMLElement)) return;

    const companion = this.state[key];
    const checkbox = card.querySelector('[data-role="checkbox"]');
    const currentPrice = card.querySelector('[data-role="current-price"]');
    const comparePrice = card.querySelector('[data-role="compare-price"]');
    const status = card.querySelector('[data-role="status"]');
    const image = card.querySelector('.complete-the-look__image');
    const sizeOptions = card.querySelector('[data-role="size-options"]');

    if (checkbox instanceof HTMLInputElement) {
      checkbox.checked = companion.checked;
      checkbox.disabled = !companion.productId || !companion.hasAvailableVariant;
    }
    if (sizeOptions instanceof HTMLFieldSetElement) sizeOptions.hidden = !companion.checked;

    card.querySelectorAll('[data-role="size-option"]').forEach((option) => {
      if (option instanceof HTMLInputElement) option.checked = option.value === companion.selectedVariantId;
    });
    if (currentPrice) currentPrice.textContent = companion.price;

    if (comparePrice) {
      comparePrice.textContent = companion.compareAtPrice;
      comparePrice.classList.toggle('hidden', !companion.compareAtPrice);
    }

    if (status && companion.productId) {
      status.textContent = companion.available ? '' : 'Sold out';
      status.classList.toggle('hidden', companion.available);
    }

    if (image instanceof HTMLImageElement && companion.image) {
      if (image.src !== companion.image) {
        image.src = companion.image;
        image.removeAttribute('srcset');
      }
      image.alt = companion.imageAlt;
    }

    card.classList.toggle('complete-the-look__product--unavailable', !companion.available);
  }

  /** @param {'fittedSheet' | 'flatSheet' | 'initial'} source */
  #dispatchStateChange(source) {
    this.#renderSelectionOutputs();
    this.dispatchEvent(
      new CustomEvent('complete-the-look:change', {
        bubbles: true,
        detail: {
          changedCompanion: source === 'fittedSheet' || source === 'flatSheet' ? source : null,
          source,
          state: this.getState(),
        },
      })
    );
  }

  /** @param {string | undefined} key */
  #isCompanionKey(key) {
    return key === 'fittedSheet' || key === 'flatSheet';
  }

  /** @param {string | undefined} value */
  #parseCents(value) {
    if (!value) return null;
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  /** @param {string | undefined} value */
  #parsePosition(value) {
    const parsed = Number.parseInt(value || '', 10);
    return parsed >= 1 && parsed <= 3 ? parsed : 0;
  }

  /** @returns {HTMLElement | null} */
  #getProductForm() {
    const scope = this.closest('.product-details') || this.closest('.shopify-section');
    if (!scope) return null;

    const productId = String(this.state?.mainProduct?.productId || this.dataset.mainProductId || '');
    const forms = Array.from(scope.querySelectorAll('product-form-component'));
    const exactMatch = forms.find(
      (form) => form instanceof HTMLElement && form.dataset.productId === productId
    );
    return exactMatch instanceof HTMLElement ? exactMatch : forms.length === 1 && forms[0] instanceof HTMLElement ? forms[0] : null;
  }

  /** @param {HTMLElement} productForm */
  #readMainQuantity(productForm) {
    const input = productForm.querySelector('quantity-selector-component input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return 1;
    const quantity = Number.parseInt(input.value, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  /**
   * Derives the exact lines that a later cart integration will consume.
   * @returns {Array<{key: string, title: string, size: string, quantity: number, variantId: string | null, checked: boolean, available: boolean}>}
   */
  getSelectedItems() {
    this.#refreshMainPrice();
    const snapshot = this.getState();
    const items = [
      {
        key: 'mainProduct',
        title: snapshot.mainProduct.summaryLabel || snapshot.mainProduct.productTitle,
        size: snapshot.mainProduct.selectedSize,
        quantity: snapshot.mainProduct.quantity,
        variantId: snapshot.mainProduct.selectedVariantId,
        checked: true,
        available: true,
        priceCents: snapshot.mainProduct.priceCents,
      },
    ];

    for (const key of ['fittedSheet', 'flatSheet']) {
      const companion = snapshot[key];
      if (!companion.checked || !companion.selectedVariantId || !companion.available) continue;
      items.push({
        key,
        title: companion.summaryLabel || companion.productTitle,
        size: companion.selectedSize,
        quantity: 1,
        variantId: companion.selectedVariantId,
        checked: companion.checked,
        available: companion.available,
        priceCents: companion.priceCents,
      });
    }

    return items;
  }

  #renderSelectionOutputs() {
    const items = this.getSelectedItems();
    const summaryItems = items.filter(
      (item) => item.key !== 'mainProduct' || this.state.mainProduct.explicitlySelected
    );
    this.#renderSummary(summaryItems);
    this.#renderAddToCartTotal(items);
  }

  /** @param {ReturnType<CompleteTheLook['getSelectedItems']>} items */
  #renderSummary(items) {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const summaries = Array.from(productForm.querySelectorAll('[data-complete-the-look-summary]'));
    const exactSummary = summaries.find(
      (candidate) => candidate instanceof HTMLElement && candidate.dataset.productId === String(this.state.mainProduct.productId)
    );
    const summary = exactSummary || (summaries.length === 1 ? summaries[0] : null);
    if (!(summary instanceof HTMLElement)) return;

    const countElement = summary.querySelector('[data-role="summary-count"]');
    const itemsElement = summary.querySelector('[data-role="summary-items"]');
    if (!countElement || !itemsElement) return;

    summary.hidden = items.length === 0;
    if (items.length === 0) {
      countElement.textContent = '';
      itemsElement.textContent = '';
      return;
    }

    const itemCount = items.reduce((total, item) => total + item.quantity, 0);
    const showLineQuantities = items.some((item) => item.quantity > 1);
    const countText = `You're adding ${itemCount} ${itemCount === 1 ? 'item' : 'items'}:`;
    const descriptions = items.map((item) => {
        const identity = this.#hasDisplaySize(item.size) ? `${item.title} - ${item.size}` : item.title;
        return showLineQuantities ? `${item.quantity} × ${identity}` : identity;
      });
    const itemsText = this.#joinDescriptions(descriptions);

    if (countElement.textContent !== countText) countElement.textContent = countText;
    if (itemsElement.textContent !== itemsText) itemsElement.textContent = itemsText;
  }

  /** @param {string[]} descriptions */
  #joinDescriptions(descriptions) {
    if (descriptions.length <= 1) return descriptions[0] || '';
    if (descriptions.length === 2) return `${descriptions[0]} and ${descriptions[1]}`;
    return `${descriptions.slice(0, -1).join(', ')} and ${descriptions.at(-1)}`;
  }

  /** @param {string} size */
  #hasDisplaySize(size) {
    const normalizedSize = size.trim().toLowerCase();
    return Boolean(normalizedSize) && normalizedSize !== 'default title';
  }

  #refreshMainPrice() {
    const price = this.#getProductForm()?.querySelector('.po-custom-atc-price');
    if (!(price instanceof HTMLElement)) return;
    this.state.mainProduct.priceCents = this.#parseCents(price.dataset.mainVariantPriceCents);
  }

  /** @param {ReturnType<CompleteTheLook['getSelectedItems']>} items */
  #renderAddToCartTotal(items) {
    const price = this.#getProductForm()?.querySelector('.po-custom-atc-price');
    if (!(price instanceof HTMLElement)) return;

    const isDefaultSelection = items.length === 1 && items[0]?.quantity === 1;
    if (isDefaultSelection) {
      price.textContent = price.dataset.mainPriceText || price.textContent;
      return;
    }

    if (items.some((item) => !Number.isInteger(item.priceCents))) return;
    const totalCents = items.reduce(
      (total, item) => total + /** @type {number} */ (item.priceCents) * item.quantity,
      0
    );
    const currency = price.dataset.currencyCode;
    if (!currency) return;

    try {
      price.textContent = new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
      }).format(totalCents / 100);
    } catch (error) {
      console.warn('Complete the Look could not format the Add to Cart total.', error);
    }
  }

}

if (!customElements.get('complete-the-look')) {
  customElements.define('complete-the-look', CompleteTheLook);
}

/**
 * Main-product-only summary controller for Sheet recommendation mode.
 * The linked Quilt Cover Set remains navigation-only and is not represented here.
 */
class CompleteTheLookSheetSummary extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Record<string, string>} */
  #mainVariantSizeMap = {};

  mainProduct = {
    productId: null,
    summaryLabel: '',
    selectedVariantId: null,
    selectedSize: '',
    explicitlySelected: false,
    quantity: 1,
  };

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#mainVariantSizeMap = this.#readMainVariantSizeMap();

    const initialVariantId = this.dataset.mainVariantId || null;
    const explicitVariantId = !this.closest('quick-add-dialog')
      ? getExplicitVariantId(window.location.href, this.#getProductForm()?.dataset.selectedVariantId)
      : null;
    this.mainProduct = {
      productId: this.dataset.mainProductId || null,
      summaryLabel: this.dataset.mainSummaryLabel || '',
      selectedVariantId: initialVariantId,
      selectedSize: initialVariantId ? this.#mainVariantSizeMap[initialVariantId] || '' : '',
      explicitlySelected: Boolean(explicitVariantId),
      quantity: 1,
    };

    const { signal } = this.#abortController;
    this.closest('.shopify-section')?.addEventListener('variant:update', this.#handleMainVariantUpdate, { signal });

    const productForm = this.#getProductForm();
    if (productForm) {
      productForm.addEventListener('quantity-selector:update', this.#handleMainQuantityChange, { signal });
      productForm.addEventListener('input', this.#handleMainQuantityChange, { signal });
      productForm.addEventListener('product-form:variant-rendered', this.#handleProductFormVariantRendered, { signal });
      this.mainProduct.quantity = this.#readMainQuantity(productForm);
    }

    this.#renderSummary();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #readMainVariantSizeMap() {
    const data = this.querySelector('[data-main-variant-size-map]')?.textContent;
    if (!data) return {};

    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      console.warn('Complete the Look could not parse the Sheet variant Size map.', error);
      return {};
    }
  }

  #handleMainVariantUpdate = (event) => {
    const detail = event.detail;
    const eventProductId = detail?.data?.productId ? String(detail.data.productId) : null;
    const currentProductId = this.mainProduct.productId ? String(this.mainProduct.productId) : null;
    const isCombinedListingUpdate = Boolean(detail?.data?.newProduct);

    if (!isCombinedListingUpdate && eventProductId !== currentProductId) return;
    if (!(event.target instanceof HTMLElement) || event.target.tagName.toLowerCase() !== 'variant-picker') return;

    const pickerSection = event.target.dataset.sectionId;
    const currentSection = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
    if (pickerSection && currentSection && pickerSection !== currentSection) return;

    const resolvedVariantId = detail?.resource?.id ? String(detail.resource.id) : null;
    this.mainProduct.productId = detail?.data?.newProduct?.id || eventProductId || currentProductId;
    this.mainProduct.selectedVariantId = resolvedVariantId;
    this.mainProduct.selectedSize = resolvedVariantId ? this.#mainVariantSizeMap[resolvedVariantId] || '' : '';
    this.mainProduct.explicitlySelected = event.target.dataset.explicitSelection === 'true';
    this.#renderSummary();
  };

  #handleProductFormVariantRendered = (event) => {
    const productForm = this.#getProductForm();
    if (!productForm || event.target !== productForm) return;
    const renderedVariantId = event.detail?.variantId ? String(event.detail.variantId) : null;
    if (renderedVariantId !== this.mainProduct.selectedVariantId) return;
    this.#renderSummary();
  };

  #handleMainQuantityChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'quantity') return;
    const productForm = this.#getProductForm();
    if (!productForm || !productForm.contains(event.target)) return;

    const quantity = this.#readMainQuantity(productForm);
    if (quantity === this.mainProduct.quantity) return;
    this.mainProduct.quantity = quantity;
    this.#renderSummary();
  };

  #getProductForm() {
    const scope = this.closest('.product-details') || this.closest('.shopify-section');
    if (!scope) return null;

    const productId = String(this.mainProduct.productId || this.dataset.mainProductId || '');
    const forms = Array.from(scope.querySelectorAll('product-form-component'));
    const exactMatch = forms.find((form) => form instanceof HTMLElement && form.dataset.productId === productId);
    return exactMatch instanceof HTMLElement ? exactMatch : forms.length === 1 && forms[0] instanceof HTMLElement ? forms[0] : null;
  }

  /** @param {HTMLElement} productForm */
  #readMainQuantity(productForm) {
    const input = productForm.querySelector('quantity-selector-component input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return 1;
    const quantity = Number.parseInt(input.value, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  #renderSummary() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const summaries = Array.from(productForm.querySelectorAll('[data-complete-the-look-summary]'));
    const summary = summaries.find(
      (candidate) => candidate instanceof HTMLElement && candidate.dataset.productId === String(this.mainProduct.productId)
    ) || (summaries.length === 1 ? summaries[0] : null);
    if (!(summary instanceof HTMLElement)) return;

    const countElement = summary.querySelector('[data-role="summary-count"]');
    const itemsElement = summary.querySelector('[data-role="summary-items"]');
    if (!countElement || !itemsElement) return;

    summary.hidden = !this.mainProduct.explicitlySelected;
    if (!this.mainProduct.explicitlySelected) {
      countElement.textContent = '';
      itemsElement.textContent = '';
      return;
    }

    const quantity = this.mainProduct.quantity;
    const normalizedSize = this.mainProduct.selectedSize.trim().toLowerCase();
    const hasDisplaySize = Boolean(normalizedSize) && normalizedSize !== 'default title';
    const identity = hasDisplaySize
      ? `${this.mainProduct.summaryLabel} - ${this.mainProduct.selectedSize}`
      : this.mainProduct.summaryLabel;
    const countText = `You're adding ${quantity} ${quantity === 1 ? 'item' : 'items'}:`;
    const itemsText = quantity > 1 ? `${quantity} × ${identity}` : identity;

    if (countElement.textContent !== countText) countElement.textContent = countText;
    if (itemsElement.textContent !== itemsText) itemsElement.textContent = itemsText;
  }
}

if (!customElements.get('complete-the-look-sheet-summary')) {
  customElements.define('complete-the-look-sheet-summary', CompleteTheLookSheetSummary);
}

/**
 * Uses the existing main-product-only Summary behavior when no CTL block is
 * present in the current product area.
 */
class MainProductSummaryFallback extends CompleteTheLookSheetSummary {
  connectedCallback() {
    const scope = this.closest('.product-details') || this.closest('.shopify-section');
    if (scope?.querySelector('complete-the-look, complete-the-look-sheet-summary')) return;
    super.connectedCallback();
  }
}

if (!customElements.get('main-product-summary-fallback')) {
  customElements.define('main-product-summary-fallback', MainProductSummaryFallback);
}

/**
 * @typedef {Object} MainProductState
 * @property {string | null} productId
 * @property {string} productTitle
 * @property {string} summaryLabel
 * @property {string | null} initialVariantId
 * @property {string | null} selectedVariantId
 * @property {string} selectedSize
 * @property {boolean} explicitlySelected
 * @property {number} sizeOptionPosition
 * @property {number} quantity
 * @property {number | null} priceCents
 */

/**
 * @typedef {Object} CompanionProductState
 * @property {boolean} checked
 * @property {string | null} productId
 * @property {string} productTitle
 * @property {string} summaryLabel
 * @property {string | null} selectedVariantId
 * @property {string} selectedSize
 * @property {boolean} available
 * @property {string} price
 * @property {number | null} priceCents
 * @property {string} compareAtPrice
 * @property {number | null} compareAtPriceCents
 * @property {boolean} hasAvailableVariant
 * @property {string} [image]
 * @property {string} [imageAlt]
 */

/**
 * @typedef {Object} CompleteTheLookState
 * @property {MainProductState} mainProduct
 * @property {CompanionProductState} fittedSheet
 * @property {CompanionProductState} flatSheet
 */
